import React, { useRef, useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Camera, ScanLine, Check, ChevronRight, ChevronLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const PhotoBox = ({ label, file, onPick, testid }) => {
  const ref = useRef(null);
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  return (
    <div className="flex-1 min-w-[150px]">
      <Label className="text-xs font-semibold mb-1 block">{label}</Label>
      <button type="button" onClick={() => ref.current?.click()} data-testid={testid}
        className={`w-full h-28 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 text-sm overflow-hidden transition-colors ${file ? "border-primary bg-primary/5 text-primary" : "border-muted text-muted-foreground hover:border-primary/50"}`}>
        {preview ? <img src={preview} alt={label} data-testid={`${testid}-thumb`} className="h-full w-full object-cover rounded-md" /> : <><Camera size={22} /> Scatta / carica</>}
      </button>
      {file && <p className="text-[11px] text-primary mt-1 flex items-center gap-1"><Check size={12} /> {file.name}</p>}
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onPick(e.target.files?.[0] || null)} />
    </div>
  );
};

export default function CheckIn() {
  const { structures, selectedStructure } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [fronte, setFronte] = useState(null);
  const [retro, setRetro] = useState(null);
  const [tessera, setTessera] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ nome: "", cognome: "", data_nascita: "", luogo_nascita: "", sesso: "", codice_fiscale: "", indirizzo: "", numero_documento: "", retta_mensile: "", caparra: false, caparra_importo: "", consenso_privacy: false, mese: "", firmatario_tipo: "", familiare_referente: "", telefono_familiare: "", email: "", structure_id: selectedStructure && selectedStructure !== "all" ? selectedStructure : "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const scan = async () => {
    if (!fronte && !retro && !tessera) { toast.error("Carica almeno una foto"); return; }
    setScanning(true);
    try {
      const fd = new FormData();
      if (fronte) fd.append("fronte", fronte);
      if (retro) fd.append("retro", retro);
      if (tessera) fd.append("tessera", tessera);
      const { data } = await api.post("/checkin/ocr", fd);
      setF((p) => ({ ...p, ...Object.fromEntries(Object.entries(data).filter(([, v]) => v)) }));
      toast.success("Dati estratti dai documenti — controlla e completa");
    } catch { toast.error("Estrazione non riuscita: compila manualmente"); }
    finally { setScanning(false); }
  };

  const goStep2 = () => {
    if (!f.nome.trim() || !f.cognome.trim()) { toast.error("Nome e cognome sono obbligatori"); return; }
    setStep(2);
  };

  const finish = async () => {
    if (!f.structure_id) { toast.error("Seleziona la struttura"); return; }
    if (!f.firmatario_tipo) { toast.error("Indica chi firma la convenzione"); return; }
    if (f.firmatario_tipo !== "ospite") {
      if (!f.familiare_referente.trim() || !f.telefono_familiare.trim() || !f.email.trim()) {
        toast.error("Nome, telefono ed email del firmatario sono obbligatori"); return;
      }
    }
    if (!f.consenso_privacy) { toast.error("È necessario il consenso al trattamento dei dati (GDPR)"); return; }
    setSaving(true);
    try {
      const payload = { ...f, mese_ingresso: f.mese, data_ingresso: f.mese ? `${f.mese}-01` : "" };
      const { data } = await api.post("/checkin/crea", payload);
      toast.success(f.retta_mensile && f.mese ? "Check-in completato! Scheda creata e retta aggiunta a Ricavi e Rette." : "Check-in completato! Scheda ospite creata.");
      const q = new URLSearchParams({ firma: "1" });
      if (f.retta_mensile) q.set("retta", f.retta_mensile);
      nav(`/ospiti/${data.id}?${q.toString()}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-5">
        <h1 className="text-2xl font-head font-bold flex items-center gap-2"><UserPlus size={22} className="text-primary" /> Check-in Ospite</h1>
        <p className="text-sm text-muted-foreground">Registra un nuovo ospite in due fasi: documenti + condizioni di ingresso.</p>
      </div>

      <div className="flex items-center gap-2 mb-5" data-testid="checkin-steps">
        {[1, 2].map((n) => (
          <div key={n} className={`flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold ${step === n ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${step === n ? "bg-white text-primary" : "bg-muted-foreground/20"}`}>{n}</span>
            {n === 1 ? "Documenti e dati" : "Retta e ingresso"}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="gr-card p-5 space-y-5" data-testid="checkin-step1">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><ScanLine size={14} /> Fotografa i documenti</div>
            <div className="flex gap-3 flex-wrap">
              <PhotoBox label="Carta d'identità (fronte)" file={fronte} onPick={setFronte} testid="checkin-photo-fronte" />
              <PhotoBox label="Carta d'identità (retro)" file={retro} onPick={setRetro} testid="checkin-photo-retro" />
              <PhotoBox label="Tessera sanitaria" file={tessera} onPick={setTessera} testid="checkin-photo-tessera" />
            </div>
            <Button onClick={scan} disabled={scanning} className="mt-3 font-semibold" data-testid="checkin-scan">
              {scanning ? <><Loader2 size={16} className="mr-1 animate-spin" /> Lettura in corso...</> : <><ScanLine size={16} className="mr-1" /> Estrai dati dai documenti</>}
            </Button>
            <p className="text-xs text-muted-foreground mt-2" data-testid="checkin-manual-note">📷 Fotocamera non disponibile o dati incompleti? Nessun problema: puoi compilare o correggere manualmente tutti i campi qui sotto.</p>
          </div>

          <div className="border-t pt-4">
            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-3">Scheda ospite (verifica e completa)</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold mb-1 block">Nome *</Label><Input value={f.nome} onChange={(e) => set("nome", e.target.value)} data-testid="checkin-nome" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Cognome *</Label><Input value={f.cognome} onChange={(e) => set("cognome", e.target.value)} data-testid="checkin-cognome" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Data di nascita</Label><Input type="date" value={f.data_nascita} onChange={(e) => set("data_nascita", e.target.value)} data-testid="checkin-nascita" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Luogo di nascita</Label><Input value={f.luogo_nascita} onChange={(e) => set("luogo_nascita", e.target.value)} data-testid="checkin-luogo" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Codice fiscale</Label><Input value={f.codice_fiscale} onChange={(e) => set("codice_fiscale", e.target.value.toUpperCase())} data-testid="checkin-cf" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Sesso</Label><Input value={f.sesso} onChange={(e) => set("sesso", e.target.value.toUpperCase())} placeholder="M / F" data-testid="checkin-sesso" /></div>
              <div className="sm:col-span-2"><Label className="text-xs font-semibold mb-1 block">Indirizzo</Label><Input value={f.indirizzo} onChange={(e) => set("indirizzo", e.target.value)} data-testid="checkin-indirizzo" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">N. documento</Label><Input value={f.numero_documento} onChange={(e) => set("numero_documento", e.target.value)} data-testid="checkin-doc" /></div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={goStep2} className="font-semibold" data-testid="checkin-next">Avanti <ChevronRight size={16} className="ml-1" /></Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="gr-card p-5 space-y-4" data-testid="checkin-step2">
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Condizioni di ingresso — {f.nome} {f.cognome}</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold mb-1 block">Retta mensile (€)</Label><Input type="number" value={f.retta_mensile} onChange={(e) => set("retta_mensile", e.target.value)} placeholder="es. 1800" data-testid="checkin-retta" /></div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Struttura *</Label>
              <select value={f.structure_id} onChange={(e) => set("structure_id", e.target.value)} className="h-10 w-full rounded-md border px-3 text-sm" data-testid="checkin-struttura">
                <option value="">Seleziona…</option>
                {structures.filter((s) => s.id !== "all").map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Mese di ingresso</Label>
              <Input type="month" value={f.mese} onChange={(e) => set("mese", e.target.value)} data-testid="checkin-mese" />
              {f.mese && <p className="text-[11px] text-muted-foreground mt-1">Ingresso: {MESI[parseInt(f.mese.split("-")[1], 10) - 1]} {f.mese.split("-")[0]}</p>}
            </div>
            <div className="rounded-lg border bg-secondary/30 p-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer font-semibold">
                <Checkbox checked={f.caparra} onCheckedChange={(v) => set("caparra", !!v)} data-testid="checkin-caparra" /> Lascia la caparra
              </label>
              {f.caparra && <div className="mt-2"><Label className="text-xs font-semibold mb-1 block">Importo caparra (€)</Label><Input type="number" value={f.caparra_importo} onChange={(e) => set("caparra_importo", e.target.value)} data-testid="checkin-caparra-importo" /></div>}
            </div>
          </div>
          <div className="rounded-lg border p-4 space-y-3" data-testid="checkin-firmatario-wrap">
            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Chi firma la convenzione? *</div>
            <div className="grid sm:grid-cols-3 gap-2">
              {[["ospite", "Ospite autonomo", "Firma da solo/a"], ["familiare", "Familiare / Parente", "Firma un parente"], ["amministratore", "Amministratore di sostegno", "Firma l'A.d.S."]].map(([key, label, sub]) => {
                const active = f.firmatario_tipo === key;
                return (
                  <button key={key} type="button" onClick={() => set("firmatario_tipo", key)} data-testid={`checkin-firmatario-${key}`}
                    className={`rounded-xl border-2 px-3 py-3 text-center transition-all ${active ? "border-primary bg-primary/5 shadow-sm" : "border-muted hover:bg-secondary/60"}`}>
                    <span className={`block text-sm font-bold ${active ? "text-primary" : ""}`}>{label}</span>
                    <span className="block text-[11px] text-muted-foreground mt-0.5">{sub}</span>
                  </button>
                );
              })}
            </div>
            {(f.firmatario_tipo === "familiare" || f.firmatario_tipo === "amministratore") && (
              <div className="grid sm:grid-cols-3 gap-3 pt-1" data-testid="checkin-firmatario-fields">
                <div><Label className="text-xs font-semibold mb-1 block">Nome e cognome *</Label><Input value={f.familiare_referente} onChange={(e) => set("familiare_referente", e.target.value)} placeholder={f.firmatario_tipo === "amministratore" ? "Nome A.d.S." : "Nome familiare"} data-testid="checkin-firmatario-nome" /></div>
                <div><Label className="text-xs font-semibold mb-1 block">Telefono *</Label><Input value={f.telefono_familiare} onChange={(e) => set("telefono_familiare", e.target.value)} placeholder="es. 333 1234567" data-testid="checkin-firmatario-tel" /></div>
                <div><Label className="text-xs font-semibold mb-1 block">Email *</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="es. nome@email.it" data-testid="checkin-firmatario-email" /></div>
              </div>
            )}
          </div>
          <label className="flex items-start gap-2 text-sm cursor-pointer rounded-lg border bg-secondary/30 p-3" data-testid="checkin-privacy-wrap">
            <Checkbox checked={f.consenso_privacy} onCheckedChange={(v) => set("consenso_privacy", !!v)} data-testid="checkin-privacy" className="mt-0.5" />
            <span><b>Consenso privacy (GDPR)</b> — L'ospite e/o il familiare/referente autorizza il trattamento dei dati personali e sanitari ai fini dell'ospitalità e dell'assistenza, ai sensi del Reg. UE 2016/679. Consenso obbligatorio per completare il check-in.</span>
          </label>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)} data-testid="checkin-back"><ChevronLeft size={16} className="mr-1" /> Indietro</Button>
            <Button onClick={finish} disabled={saving} className="font-semibold" data-testid="checkin-finish">{saving ? "Salvataggio..." : <><Check size={16} className="mr-1" /> Completa check-in</>}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
